// Ζώνες που εμφανίζονται στο dropdown επιλογής αγοράς (Dashboard + Optimize).
// HU προστέθηκε 8/2026: έχει DAM actuals από 1/1/2021, IDA1/2/3 από 13/6/2024 και
// balancing από 1/1/2021. ΠΡΟΣΟΧΗ: η λίστα τροφοδοτεί ΟΛΕΣ τις καρτέλες — η
// καρτέλα Fundamentals διαβάζει MARKET_MIX_METRICS / MARKET_GENERATION_METRICS
// στο Dashboard.jsx (έχουν πλέον εγγραφή για HU).
// BG προστέθηκε 17/9/2026: έχει DAM actuals από 1/1/2021 (200.040 MTU, με τη
// μετατροπή BGN->EUR για τις μέρες έως 20/1/2022) και EEX forward anchor FKBY.
// ΔΕΝ έχει IDA ούτε balancing - γι' αυτό λείπει από τα δύο Set παρακάτω - και
// ΔΕΝ έχει καμία πρόβλεψη τιμής: είναι η πρώτη αγορά "μόνο πραγματικών τιμών",
// εξού και το DAM_FORECAST_MARKETS.
export const SUPPORTED_MARKETS = ["GR", "FR", "DE", "NL", "HU", "BG"];

// ΟΝΟΜΑ ΖΩΝΗΣ ENTSO-E -> ΚΩΔΙΚΟΣ ΑΓΟΡΑΣ. Το project ενός γερμανικού έργου
// αποθηκεύει biddingZone "DE_LU" (η ζώνη προσφορών Γερμανίας-Λουξεμβούργου),
// αλλά ΟΛΟΙ οι πίνακες τιμών και οι validators του backend είναι με κωδικό "DE".
// Χωρίς αυτό το mapping τα γερμανικά έργα έβγαζαν "House forward path for DE_LU
// is unavailable", δεν έτρεχαν balancing bankability και δεν επέλεγαν IDA1/2/3.
// Κρατήστε το συγχρονισμένο με το market_stack.MARKET_ZONE_ALIASES του backend.
const MARKET_ZONE_ALIASES = {
  DE_LU: "DE",
  "DE-LU": "DE",
};

// Αγορές με φορτωμένες τιμές IDA1/2/3 / reserve capacity. ΠΡΕΠΕΙ να μένουν
// συγχρονισμένες με τα IDA_SUPPORTED_MARKETS / BALANCING_SUPPORTED_MARKETS του
// api/services/market_stack.py. Εξάγονται από ΕΔΩ (και δεν αντιγράφονται σε κάθε
// σελίδα) γιατί δύο αντίγραφα της λίστας απέκλιναν: το General.jsx έλεγε στον
// χρήστη "available for GR, FR, DE and NL" αφότου είχε προστεθεί το HU.
// Η BG μπήκε και στα δύο στις 17/9/2026, μόλις φορτώθηκαν τα δεδομένα:
// IDA 192.960 γραμμές από 15/6/2024 (Nord Pool, deliveryArea "BG"),
// balancing 141.582 γραμμές από 1/1/2024. ΠΡΟΣΟΧΗ στη Βουλγαρία: το
// balancing είναι ΜΟΝΟ ισχύος - δεν δημοσιεύονται καθόλου τιμές
// αποκλίσεων (imbalance) - και το mFRR έχει μόνο σκέλος Up.
export const IDA_SUPPORTED_MARKETS = new Set(["GR", "FR", "DE", "NL", "HU", "BG"]);
export const BALANCING_SUPPORTED_MARKETS = new Set([
  "GR",
  "FR",
  "DE",
  "NL",
  "HU",
  "BG",
]);

// Αγορές για τις οποίες η πλατφόρμα ΔΗΜΟΣΙΕΥΕΙ πρόβλεψη τιμής DAM (in-house
// MetEn ή Forecast Valley). Είναι ΞΕΧΩΡΙΣΤΟ σύνολο από το SUPPORTED_MARKETS:
// μια αγορά μπορεί να έχει πλήρες ιστορικό πραγματικών τιμών και καμία
// πρόβλεψη. Η BG είναι ακριβώς αυτή η περίπτωση - δεν έχει ούτε feed
// Forecast Valley ούτε εγγραφή στο dam_forecast_v1/markets.py του backend.
// Χωρίς αυτόν τον έλεγχο η καρτέλα DAM δείχνει τρία checkbox "Forecast
// P10/P50/P90" που δεν εναλλάσσουν τίποτα και ένα "Forecast Metrics" που δεν
// εμφανίζει ποτέ μετρικές - δηλαδή υπόσχεται δεδομένα που δεν υπάρχουν.
export const DAM_FORECAST_MARKETS = new Set(["GR", "FR", "DE", "NL", "HU"]);

export function normalizeMarketZone(value) {
  const zone = String(value || "").trim().toUpperCase();
  return MARKET_ZONE_ALIASES[zone] || zone;
}

// true αν η αγορά του έργου έχει δεδομένα IDA. Δέχεται είτε κωδικό αγοράς είτε
// όνομα ζώνης ENTSO-E ("DE_LU").
export function supportsIdaMarket(value) {
  return IDA_SUPPORTED_MARKETS.has(normalizeMarketZone(value));
}

// true αν η πλατφόρμα δημοσιεύει πρόβλεψη DAM για την αγορά του έργου.
// Δέχεται είτε κωδικό αγοράς είτε όνομα ζώνης ENTSO-E ("DE_LU").
export function supportsDamForecast(value) {
  return DAM_FORECAST_MARKETS.has(normalizeMarketZone(value));
}

export function getSupportedMarketZone(value) {
  const zone = normalizeMarketZone(value);
  return SUPPORTED_MARKETS.includes(zone) ? zone : null;
}

export function isUnsupportedMarketZone(value) {
  const zone = normalizeMarketZone(value);
  return Boolean(zone) && !SUPPORTED_MARKETS.includes(zone);
}

// Η λίστα των αγορών με πρόβλεψη DAM, για μήνυμα προς τον χρήστη.
// Εξάγεται από ΕΔΩ για τον ίδιο λόγο που εξάγεται και το
// getSupportedMarketsLabel: δύο αντίγραφα μιας λίστας απέκλιναν ήδη μία φορά.
export function getDamForecastMarketsLabel() {
  return [...DAM_FORECAST_MARKETS].join(", ");
}

export function getSupportedMarketsLabel() {
  return SUPPORTED_MARKETS.join(", ");
}

// --------------------------------------------------------------------------
// Ελληνική επιλεξιμότητα παροχής υπηρεσιών εξισορρόπησης (ΚΑΕ v23)
// --------------------------------------------------------------------------
// Ισχύς εφεδρείας μπορεί να προσφερθεί μόνο από Κατανεμόμενη Οντότητα
// Αποθήκευσης (ορ. 81). Ο ΣΑΗΕ επιτελεί εξ ορισμού ΑΠΟΚΛΕΙΣΤΙΚΑ αποθήκευση
// (ορ. 146) και το Χαρτοφυλάκιο Αποθήκευσης δέχεται μόνο Μικρούς ΣΑΗΕ
// (ορ. 174 με ορ. 112), οπότε για σταθμό ΑΠΕ με ενσωματωμένη αποθήκευση η
// μόνη οδός είναι ο Σταθμός ΑΠΕ με μεγάλη αποθήκευση (ορ. 145), του οποίου
// τα τέσσερα κριτήρια ισχύουν ΣΩΡΕΥΤΙΚΑ.
//
// ΠΡΕΠΕΙ να μένει συγχρονισμένο με το
// api/services/market_stack.greek_balancing_provider_eligibility του backend:
// εδώ γκριζάρει το checkbox, εκεί είναι η εγγύηση - μια μελέτη που περνούσε
// από εδώ και έσκαγε εκεί θα ήταν χειρότερη από καμία από τις δύο.
export const GREEK_LARGE_STORAGE_MIN_INJECTION_MW = 10;
export const GREEK_LARGE_STORAGE_MIN_POWER_SHARE = 0.3;
export const GREEK_LARGE_STORAGE_MIN_ENERGY_SHARE = 0.6;

// Τα κριτήρια του ορ. 145 αριθμούνται με ελληνικά γράμματα στην ελληνική
// γλώσσα. ΠΡΕΠΕΙ να ταυτίζεται με το _BALANCING_CRITERION_LETTERS_EL του
// BESS_feasibility/export.py, ώστε η οθόνη και η αναφορά να δείχνουν το ίδιο
// κριτήριο με το ίδιο γράμμα.
export const GREEK_BALANCING_CRITERION_LETTERS_EL = {
  a: "α",
  b: "β",
  c: "γ",
  d: "δ",
};

// Τα γράμματα των κριτηρίων που απέτυχαν, στη γλώσσα της οθόνης.
export function greekBalancingCriterionLetters(failed, isGreek) {
  return (Array.isArray(failed) ? failed : [])
    .map((id) => (isGreek ? GREEK_BALANCING_CRITERION_LETTERS_EL[id] || id : id))
    .join(", ");
}
const GREEK_TRANSMISSION_CONNECTION_LEVELS = new Set([
  "HV",
  "EHV",
  "UHV",
  "TRANSMISSION",
]);

function positiveNumber(value) {
  const out = Number(value);
  return Number.isFinite(out) && out > 0 ? out : 0;
}

// Επιστρέφει { applies, eligible, failed, criteria }. applies=false για κάθε
// μη ελληνική αγορά και για σταθμούς αποκλειστικά αποθήκευσης (που κρατούν
// και τις δύο οδούς ΣΑΗΕ), ώστε καμία υπάρχουσα ρύθμιση να μην αλλάζει.
export function greekBalancingEligibility({
  market,
  hasResGeneration,
  connectionLevel,
  storagePowerMw,
  storageEnergyMwh,
  stationMaxNetPowerMw,
} = {}) {
  const zone = normalizeMarketZone(market);
  if (zone !== "GR" || !hasResGeneration) {
    return { applies: false, eligible: true, failed: [], criteria: [] };
  }
  const level = String(connectionLevel || "").trim().toUpperCase();
  const powerMw = positiveNumber(storagePowerMw);
  const energyMwh = positiveNumber(storageEnergyMwh);
  const stationMw = positiveNumber(stationMaxNetPowerMw);
  if (powerMw <= 0) {
    // Δεν υπάρχει τίποτα να κριθεί: η αποθήκευση δεν έχει ακόμη δηλωμένη ισχύ.
    // Καλύτερα σιωπή παρά να χαρακτηριστεί μη επιλέξιμος ένας σταθμός με βάση
    // ένα κενό πεδίο - το backend παραμένει η εγγύηση.
    return { applies: false, eligible: true, failed: [], criteria: [] };
  }
  const powerShare = stationMw > 0 ? powerMw / stationMw : 0;
  const energyShare = stationMw > 0 ? energyMwh / stationMw : 0;

  const criteria = [
    { id: "a", met: GREEK_TRANSMISSION_CONNECTION_LEVELS.has(level) },
    { id: "b", met: powerMw >= GREEK_LARGE_STORAGE_MIN_INJECTION_MW },
    { id: "c", met: stationMw > 0 && powerShare >= GREEK_LARGE_STORAGE_MIN_POWER_SHARE },
    { id: "d", met: stationMw > 0 && energyShare >= GREEK_LARGE_STORAGE_MIN_ENERGY_SHARE },
  ];
  const failed = criteria.filter((c) => !c.met).map((c) => c.id);
  return { applies: true, eligible: failed.length === 0, failed, criteria };
}
